'use client';

import { createDonationCheckoutSession } from '@/src/actions/stripe';
import { Icon, useToast } from '@/src/components/ui';
import { AmountUsdSelection } from '@/src/components/ui/AmountUsdSelection';
import { DialogRightCornerIcons } from '@/src/components/ui/DialogRightCornerIcons';
import PageLoader from '@/src/components/ui/PageLoader';
import { noBorderStyles } from '@/src/constants/constants';
import { useBoolean } from '@/src/hooks/use-boolean';
import { useUsdAmountSelection } from '@/src/sections/finances/hooks/use-usd-amount-selection';
import { PayPalButtons } from '@/src/sections/finances/paypal/PayPalButtons';
import { Button } from '@workspace/ui/components/button';
import { Input } from '@workspace/ui/components/input';
import { redirect } from 'next/navigation';
import { useState } from 'react';
import { PaypalRecipientStatus } from 'types/src/types';

type Step = 'form' | 'paypal';
type Props = {
  creatorId: number;
  userId: number;
  stripeAccount?: string;
  creatorName: string;
  creatorImage?: string;
  profileUniqueLink: string;
  isAnonymous?: boolean;
  className?: string;
  isMyProfile: boolean;
  paymentProvider: 'Stripe' | 'Paypal' | null;
  paypalStatus?: PaypalRecipientStatus | null;
};

export const Donate = ({
  creatorId,
  userId,
  stripeAccount,
  profileUniqueLink,
  isAnonymous = false,
  className,
  creatorName,
  creatorImage,
  isMyProfile,
  paymentProvider,
  paypalStatus,
}: Props) => {
  const toast = useToast();
  const isOpenDialog = useBoolean(false);
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<Step>('form');

  const canPay =
    paymentProvider === 'Stripe' ||
    (paymentProvider === 'Paypal' && paypalStatus !== 'blocked');

  const {
    selectedAmount,
    customAmount,
    handlePresetClick,
    handleCustomAmountChange,
    getFinalAmount,
    isValid,
    reset,
  } = useUsdAmountSelection();

  const handleClick = () => {
    if (isMyProfile) return;

    if (!canPay) {
      toast.error("This creator can't accept payments right now.");
      return;
    }

    if (isAnonymous || !userId) {
      setIsLoading(true);
      redirect('/sign-in');
      return;
    }

    setStep('form');
    isOpenDialog.onTrue();
  };

  const handleDonateClick = async () => {
    const amount = getFinalAmount();
    if (!isValid || !amount) return;

    if (paymentProvider === 'Stripe') {
      const { url } = await createDonationCheckoutSession(
        amount,
        creatorId,
        creatorName,
        userId,
        stripeAccount as string,
        profileUniqueLink,
        message,
      );

      if (url) {
        setIsLoading(true);
        window.location.href = url;
      }
      return;
    }

    if (paymentProvider === 'Paypal') {
      if (paypalStatus === 'blocked') {
        toast.error("This creator can't receive PayPal payouts.");
        return;
      }
      setStep('paypal');
      return;
    }

    toast.error("This creator can't accept payments right now.");
  };

  const handleCloseDialog = () => {
    reset();
    isOpenDialog.onFalse();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      return;
    }
  };

  return (
    <>
      {isLoading && <PageLoader />}
      <Button
        size="sm"
        variant="secondary"
        className={`w-[49%] ${className} disabled:opacity-100 disabled:bg-black`}
        onClick={handleClick}
        disabled={isMyProfile}
      >
        Donate
      </Button>
      {isOpenDialog.value && (
        <div className="fixed inset-0 z-50 flex justify-center items-start md:items-center bg-black/30 p-2 md:p-0 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white p-5 rounded-2xl shadow-md flex flex-col relative w-[95%] md:w-[540px] max-h-[90vh] overflow-y-auto min-h-0">
            <div className="flex space-between items-center w-full relative">
              <div className="flex flex-col gap-2">
                <h3 className="text-2xl font-semibold p-0">Make a donation</h3>
                <p className="text-black/60 text-sm font-light">
                  Support your favorite creator's journey.
                </p>
              </div>
              <DialogRightCornerIcons
                leftIcon="card"
                rightIcon="wallet-black"
                className="absolute top-0 right-0"
              />
            </div>
            <hr className="w-full border-gray-100 my-5" />
            <div className="flex items-center gap-3 relative mb-5">
              <div className="">
                {creatorImage ? (
                  <img
                    src={creatorImage}
                    alt="Author Image"
                    className="size-[60px] rounded-full"
                  />
                ) : (
                  <div className="border-1 border-black rounded-full border-box size-10 p-2">
                    <Icon name="follow" className="size-5.5" />
                  </div>
                )}
              </div>
              <div className="flex flex-col text-sm font-light text-black/80 gap-2">
                to creator
                <span className="text-base font-semibold">{creatorName}</span>
              </div>
            </div>
            {step === 'form' && (
              <>
                <AmountUsdSelection
                  selectedAmount={selectedAmount}
                  customAmount={customAmount}
                  onPresetClick={handlePresetClick}
                  onCustomAmountChange={handleCustomAmountChange}
                />
                <Input
                  id="custom-amount"
                  placeholder="Leave a message fo the creator (optional)"
                  style={noBorderStyles}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="md:text-lg p-4 bg-bg-gray h-16 rounded-xl placeholder:text-black/40 placeholder:text-sm pl-6 mt-5"
                  type="text"
                  onKeyDown={handleKeyDown}
                />
                <hr className="w-full border-gray-100 my-5" />
                <div className="w-full flex justify-between">
                  <Button
                    size="sm"
                    variant="outline"
                    className="bg-transparent px-4 h-7"
                    onClick={handleCloseDialog}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDonateClick}
                    size="sm"
                    className="px-4 h-7"
                  >
                    Donate ${getFinalAmount()}
                  </Button>
                </div>
              </>
            )}
            {step === 'paypal' && (
              <>
                <div className="flex items-center justify-between md:mb-5">
                  <h4 className="text-lg! text-black/50">{`Pay $${getFinalAmount().toFixed(2)} with PayPal`}</h4>
                  <Button
                    variant="outline"
                    onClick={() => setStep('form')}
                    size="sm"
                    className="bg-transparent px-4 h-7"
                  >
                    Back
                  </Button>
                </div>

                <PayPalButtons
                  amount={getFinalAmount().toFixed(2)}
                  currency="USD"
                  productName={`Donation to ${creatorName}`}
                  creatorId={creatorId}
                  profileUniqueLink={profileUniqueLink}
                  message={message}
                  onSuccess={() => {
                    toast.success('Payment captured!');
                    handleCloseDialog();
                  }}
                  onCancel={() => {
                    toast.error('Payment cancelled');
                    setStep('form');
                  }}
                  onError={(e) => {
                    console.error(e);
                    toast.error('PayPal error');
                    setStep('form');
                  }}
                />
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
